/* eslint-disable no-restricted-syntax */
/* eslint-disable no-nested-ternary */

export const getDirectoriesFromPath = (path: string) =>
  path.split('/').slice(0, -1)

export const readFileAsText = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error)
    reader.readAsText(file)
  })

function isInteger(n: unknown): n is number {
  return typeof n === 'number' && Number.isInteger(n)
}

function isRgbArray(v: unknown): v is [number, number, number] {
  return (
    Array.isArray(v) &&
    v.length === 3 &&
    v.every((c) => isInteger(c) && c >= 0 && c <= 255)
  )
}

function isValidLabelInt(v: unknown): v is number {
  return isInteger(v) && v >= 1 && v <= 255
}

type MappingValue = number | [number, number, number]
type Mapping = Record<string, MappingValue>

export function parseAndValidateMapping(
  jsonText: string
): { mapping: Mapping; valueKind: 'rgb' | 'int' } {
  let data: unknown
  try {
    data = JSON.parse(jsonText)
  } catch {
    throw new Error('Mapping file is not valid JSON')
  }

  if (data === null || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error('Mapping must be a JSON object of key value pairs')
  }

  const entries = Object.entries(data as Record<string, unknown>)
  if (entries.length === 0) {
    throw new Error('Mapping cannot be empty')
  }

  const [, firstVal] = entries[0]
  const firstIsRgb = isRgbArray(firstVal)
  const firstIsInt = isValidLabelInt(firstVal)

  if (!firstIsRgb && !firstIsInt) {
    throw new Error('Values must be RGB arrays [r,g,b] or integers 1–255')
  }

  const expectedKind: 'rgb' | 'int' = firstIsRgb ? 'rgb' : 'int'

  for (const [k, v] of entries) {
    if (typeof k !== 'string' || k.trim() === '') {
      throw new Error('All keys must be non-empty strings')
    }
    if (expectedKind === 'rgb' && !isRgbArray(v)) {
      throw new Error('All values must be RGB arrays [r,g,b] (0–255)')
    }
    if (expectedKind === 'int' && !isValidLabelInt(v)) {
      throw new Error('All values must be integers between 0 and 255')
    }
  }

  return { mapping: data as Mapping, valueKind: expectedKind }
}
